/**
 * AngularJS filter to format telephone numbers.
 * It attempts to parse various common input formats and normalize them.
 * It can also format the number for use in `tel:` HREFs.
 *
 * @returns {function(string, boolean=): string} The filter function.
 */
export default function phoneNumberFilter() {
  /**
   * Formats a telephone number string.
   *
   * @param {string} tel The raw telephone number input string.
   * @param {boolean} [hrefFormat=false] - Optional. If true, formats the number for `tel:` HREFs
   * (e.g., using ';ext=' for extensions). If false or undefined, formats for human readability
   * (e.g., using ' x' for extensions).
   * @returns {string} The formatted phone number, or an empty string if the input is invalid or cannot be parsed.
   */
  return function (tel, hrefFormat) {
    // Default format for the base 10-digit number
    const defaultFormat = new RegExp(/^\d{3}-\d{3}-\d{4}$/);
    if (!tel) {
      return '';
    }

    // Define parts as RegExp objects
    const countryCodePart = /1?[ -]?/; // Optional '1' followed by an optional single space or '-'
    const areaCodePart = /\(?(?<area>\d{1,3})\)?/; // Area code, optionally wrapped in parentheses
    const separatorPart = /[ -._]{0,3}/; // Matches 0 to 3 separators (space, '-', '.', or '_')
    const hiNumPart = /(?<hiNum>\d{1,3})/; // 1-3 digits for the first part of the number
    const loNumPart = /(?<loNum>\d{1,4})/; // 1-4 digits for the last part of the number

    // Part to capture common extension formats. This entire group is optional.
    // It looks for patterns like "ext. 123", "x123", "extension 123", or ";ext=123" (RFC3966 style)
    // eslint-disable-next-line unicorn/no-unsafe-regex
    const extensionPart = /(?:[ #,;]?(?:ext\.?|x|extension) ?(?<ext>\d{1,5}))?/;

    // Combine the .source property of each RegExp part
    const combinedPatternString =
      countryCodePart.source +
      areaCodePart.source +
      separatorPart.source +
      hiNumPart.source +
      separatorPart.source +
      loNumPart.source +
      extensionPart.source;

    const possibleFormats = new RegExp(combinedPatternString);

    const match = tel.match(possibleFormats);

    // Check if the main regex matched and captured essential groups for the base number
    const { area, hiNum, loNum, ext } = match?.groups || {};
    if (area && hiNum && loNum) {
      const basePhoneNumber = `${area}-${hiNum}-${loNum}`;

      // Validate the constructed base phone number against the default format.
      // This is important because regex parts like area (\d{1,3}) are lenient.
      if (defaultFormat.test(basePhoneNumber)) {
        if (ext) {
          return `${basePhoneNumber}${hrefFormat ? ';ext=' : ' x'}${ext}`;
        }
        return basePhoneNumber;
      }
    }

    // Fallback: If the main parsing/construction didn't yield a valid result,
    // check if the original input 'tel' was already in the simple default format.
    if (defaultFormat.test(tel)) {
      return tel;
    }

    return ''; // All attempts to parse or validate have failed.
  };
}
